import { useState, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon } from "@mui/icons-material";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useEditData from "@/hooks/editHooks/useEditData";
import NewDeleteModal from "@components/newDeleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSession } from "@/hooks/useSession";
import axios from "@/api/axios";
import { riskLevelColor } from "@/lib/listData";
import AddHazardDialog from "./dialogs/AddHazardDialog";
import EditHazardDialog from "./dialogs/EditHazardDialog";
import SelectedPrecautionsPopup from "./dialogs/SelectedPrecautionsPopup";
import UnlinkHazardDialog from "./dialogs/UnlinkHazardDialog";
import UnlinkPrecautionDialog from "./dialogs/UnlinkPrecautionDialog";
import EditWorkflowHazardsDialog from "./dialogs/EditWorkflowHazardsDialog";
import AddPrecautionDialog from "./dialogs/AddPrecautionDialog";
import OrganizationHeader from "@/pages/admin/organization/components/OrganizationHeader";
import WorkflowsList from "./components/WorkflowsList";
import HazardsPanel from "./components/HazardsPanel";
import MobileHazardsView from "./components/MobileHazardsView";
import HazardsGuideDialog from "@/components/dialogs/HazardsGuideDialog";
import { Warning as WarningIcon } from "@mui/icons-material";

function Hazards() {
  const { t } = useTranslation();
  const endpoint = "/api/v1/org-admin/hazards";
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { token } = useSession();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  // Check if screen is small (mobile/tablet)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHazard, setEditingHazard] = useState(null);
  const [editingHazardId, setEditingHazardId] = useState(null);
  const [selectedPrecautions, setSelectedPrecautions] = useState([]);
  const [showPrecautionsPopup, setShowPrecautionsPopup] = useState(false);
  const [
    selectedPrecautionsUpdateCallback,
    setSelectedPrecautionsUpdateCallback,
  ] = useState(null);
  const [deletingPrecaution, setDeletingPrecaution] = useState(null);
  const [editWorkflowDialogOpen, setEditWorkflowDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [selectedWorkflowHazards, setSelectedWorkflowHazards] = useState([]);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [unlinkingHazard, setUnlinkingHazard] = useState(null);
  const [unlinkWorkflowId, setUnlinkWorkflowId] = useState(null);
  const [unlinkPrecautionConfirmOpen, setUnlinkPrecautionConfirmOpen] =
    useState(false);
  const [unlinkingPrecaution, setUnlinkingPrecaution] = useState(null);
  const [unlinkPrecautionHazardId, setUnlinkPrecautionHazardId] =
    useState(null);
  const [expandedHazard, setExpandedHazard] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [addPrecautionDialogOpen, setAddPrecautionDialogOpen] = useState(false);
  const [targetHazardForNewPrecaution, setTargetHazardForNewPrecaution] =
    useState(null);
  const [isAddingPrecautionToHazard, setIsAddingPrecautionToHazard] =
    useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [preSelectedCategoryId, setPreSelectedCategoryId] = useState(null);

  const { mutate: postHazard, isLoading: postIsLoading } = usePostJsonData();
  const { mutate: editHazardMutation, isLoading: editIsLoading } = useEditData(
    editingHazardId ? `${endpoint}/${editingHazardId}` : endpoint,
    ["hazards"]
  );
  const { mutate: deleteHazard } = useDeleteData(endpoint, ["hazards"]);
  const {
    mutate: editWorkflowHazardsMutation,
    isLoading: editWorkflowIsLoading,
  } = useEditData(
    editingWorkflowId
      ? `/api/v1/org-admin/workflows/${editingWorkflowId}/hazards`
      : null,
    ["workflows-hazards", "hazards"]
  );

  // Fetch hazards from API
  const {
    data: hazardsData,
    isLoading: hazardsLoading,
    error: hazardsError,
  } = useAuthenticatedQuery(["hazards"], "/api/v1/metadata/hazards");

  // Fetch workflows to group hazards
  const {
    data: workflowsData,
    isLoading: workflowsLoading,
    error: workflowsError,
  } = useAuthenticatedQuery(
    ["workflows-hazards"],
    "/api/v1/metadata/workflows"
  );

  // Fetch precautions and precaution categories for the dialog
  const { data: precautions, isLoading: precautionsLoading } =
    useAuthenticatedQuery(["precautions"], "/api/v1/metadata/precautions");

  const { data: precautionCategories, isLoading: categoriesLoading } =
    useAuthenticatedQuery(
      ["precautions-categories"],
      "/api/v1/metadata/precautions-categories"
    );

  // Define category order for precautions dropdown
  const categoryOrder = [
    "Elimination / Substitution Controls",
    "Engineering Controls",
    "Work Method / Task Controls",
    "Administrative Procedural Controls",
    "Personal Protective Equipment Controls",
    "Emergency & Recovery Preparedness",
  ];

  // Group precautions by category for the dropdown
  const groupedPrecautions =
    precautions?.reduce((acc, precaution) => {
      const categoryId = precaution.category_id;
      if (!acc[categoryId]) {
        const category = precautionCategories?.find(
          (cat) => cat.id === categoryId
        );
        acc[categoryId] = {
          label: category?.name || "Unknown Category",
          options: [],
        };
      }
      acc[categoryId].options.push({
        value: precaution.id,
        label: precaution.name,
      });
      return acc;
    }, {}) || {};

  // Convert grouped precautions to react-select format with proper ordering
  const sortedCategoryEntries = Object.entries(groupedPrecautions).sort(
    ([, groupA], [, groupB]) => {
      const nameA = groupA.label || "";
      const nameB = groupB.label || "";

      const indexA = categoryOrder.indexOf(nameA);
      const indexB = categoryOrder.indexOf(nameB);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return nameA.localeCompare(nameB);
    }
  );

  const precautionOptions = sortedCategoryEntries.map(([, group]) => group);

  // Filter workflows that have target "WORK_PERMIT"
  const workPermitWorkflows =
    workflowsData?.filter((workflow) => workflow.target === "WORK_PERMIT") ||
    [];

  // Group hazards by workflow
  const groupedHazards = (() => {
    const grouped = {};

    // First, initialize all WORK_PERMIT workflows with empty hazard arrays
    workPermitWorkflows.forEach((workflow) => {
      grouped[workflow.id] = {
        workflow: workflow,
        hazards: [],
      };
    });

    // Then, populate hazards for each workflow
    hazardsData?.forEach((hazard) => {
      // Find workflows that contain this hazard
      const associatedWorkflows = workPermitWorkflows.filter((workflow) =>
        workflow.hazards?.some(
          (workflowHazard) => workflowHazard.id === hazard.id
        )
      );

      if (associatedWorkflows.length > 0) {
        // Add hazard to ALL associated workflows (a hazard can be in multiple workflows)
        associatedWorkflows.forEach((associatedWorkflow) => {
          if (grouped[associatedWorkflow.id]) {
            grouped[associatedWorkflow.id].hazards.push(hazard);
          }
        });
      } else {
        // Hazards not associated with any workflow go to "Unassigned"
        if (!grouped["unassigned"]) {
          grouped["unassigned"] = {
            workflow: { id: "unassigned", name: "Unassigned Hazards" },
            hazards: [],
          };
        }
        grouped["unassigned"].hazards.push(hazard);
      }
    });

    // Sort to ensure "unassigned" is always at the end
    const sortedEntries = Object.entries(grouped).sort(([keyA], [keyB]) => {
      if (keyA === "unassigned") return 1;
      if (keyB === "unassigned") return -1;
      return 0;
    });

    return Object.fromEntries(sortedEntries);
  })();

  const workflowEntries = Object.entries(groupedHazards);

  // Set default selected workflow (first one in the list) using useEffect
  useEffect(() => {
    if (
      !selectedWorkflowId &&
      !hazardsLoading &&
      !workflowsLoading &&
      hazardsData &&
      workflowsData
    ) {
      const entries = Object.entries(groupedHazards);
      if (entries.length > 0) {
        setSelectedWorkflowId(entries[0][0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardsData, workflowsData, hazardsLoading, workflowsLoading]);

  // Get selected workflow's hazards
  const selectedWorkflowGroup = selectedWorkflowId
    ? groupedHazards[selectedWorkflowId]
    : null;

  const addHazard = (formattedData, resetFields) => {
    postHazard(
      { url: endpoint, data: formattedData },
      {
        onSuccess: () => {
          resetFields();
          setAddDialogOpen(false);
          setToast({
            open: true,
            message: t("hazards.successAddToast"),
            severity: "success",
          });
          queryClient.invalidateQueries(["hazards"]);
        },
        onError: (err) => {
          handleApiError(err);
        },
      }
    );
  };

  const editHazard = (formattedData, resetFields) => {
    editHazardMutation(formattedData, {
      onSuccess: () => {
        resetFields();
        setEditingHazard(null);
        setEditingHazardId(null);
        setEditDialogOpen(false);
        setToast({
          open: true,
          message: t("hazards.successEditToast"),
          severity: "success",
        });
      },
      onError: (err) => {
        handleApiError(err);
      },
    });
  };

  const handleEditDialogOpen = (hazard) => {
    setEditingHazard(hazard);
    setEditingHazardId(hazard.id);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingHazard(null);
    setEditingHazardId(null);
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setConfirmOpen(true);
  };

  const handleDelete = () => {
    deleteHazard(itemToDelete, {
      onSuccess: () => {
        setToast({
          open: true,
          message: t("hazards.successDeleteToast"),
          severity: "success",
        });
        setItemToDelete(null);
        setConfirmOpen(false);
      },
      onError: (err) => {
        handleApiError(err);
        setItemToDelete(null);
        setConfirmOpen(false);
      },
    });
  };

  const confirmUnlinkPrecaution = (hazardId, precaution) => {
    setUnlinkPrecautionHazardId(hazardId);
    setUnlinkingPrecaution(precaution);
    setUnlinkPrecautionConfirmOpen(true);
  };

  const handleDeletePrecaution = async () => {
    if (!unlinkPrecautionHazardId || !unlinkingPrecaution) return;

    try {
      setDeletingPrecaution(unlinkingPrecaution.id);

      const deleteUrl = `/api/v1/org-admin/hazards/${unlinkPrecautionHazardId}/precautions/${unlinkingPrecaution.id}`;

      await axios.delete(deleteUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setToast({
        open: true,
        message: t("hazards.successUnlinkPrecautionToast"),
        severity: "success",
      });

      // Refresh hazards data
      queryClient.invalidateQueries(["hazards"]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setDeletingPrecaution(null);
      setUnlinkPrecautionHazardId(null);
      setUnlinkingPrecaution(null);
      setUnlinkPrecautionConfirmOpen(false);
    }
  };

  const handleEditWorkflowDialogOpen = (workflow) => {
    setEditingWorkflow(workflow);
    setEditingWorkflowId(workflow.id);

    // Set selected hazards to the workflow's current hazards
    const currentHazardIds = workflow.hazards?.map((hazard) => hazard.id) || [];
    setSelectedWorkflowHazards(currentHazardIds);

    setEditWorkflowDialogOpen(true);
  };

  const handleEditWorkflowDialogClose = () => {
    setEditWorkflowDialogOpen(false);
    setEditingWorkflow(null);
    setEditingWorkflowId(null);
    setSelectedWorkflowHazards([]);
  };

  const handleWorkflowHazardToggle = (hazardId) => {
    setSelectedWorkflowHazards((prev) => {
      if (prev.includes(hazardId)) {
        return prev.filter((id) => id !== hazardId);
      } else {
        return [...prev, hazardId];
      }
    });
  };

  const handleSaveWorkflowHazards = () => {
    if (!editingWorkflowId) return;

    const payload = {
      hazard_ids: selectedWorkflowHazards,
    };

    editWorkflowHazardsMutation(payload, {
      onSuccess: () => {
        handleEditWorkflowDialogClose();
        setToast({
          open: true,
          message: t("worktypeHazards.successUpdateToast"),
          severity: "success",
        });
        queryClient.invalidateQueries(["workflows-hazards"]);
        queryClient.invalidateQueries(["hazards"]);
      },
      onError: (err) => {
        handleApiError(err);
      },
    });
  };

  const confirmUnlinkHazard = (workflowId, hazard) => {
    setUnlinkWorkflowId(workflowId);
    setUnlinkingHazard(hazard);
    setUnlinkConfirmOpen(true);
  };

  const handleUnlinkHazard = async () => {
    if (!unlinkWorkflowId || !unlinkingHazard) return;

    try {
      const deleteUrl = `/api/v1/org-admin/workflows/${unlinkWorkflowId}/hazards/${unlinkingHazard.id}`;

      await axios.delete(deleteUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setToast({
        open: true,
        message: t("hazards.successUnlinkHazardToast"),
        severity: "success",
      });

      // Refresh data
      queryClient.invalidateQueries(["workflows-hazards"]);
      queryClient.invalidateQueries(["hazards"]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setUnlinkWorkflowId(null);
      setUnlinkingHazard(null);
      setUnlinkConfirmOpen(false);
    }
  };

  // Helper function to get risk level color for a hazard
  const getRiskLevelColor = (likelihood, severity) => {
    if (!likelihood || !severity) return null;
    return riskLevelColor[likelihood]?.[severity] || null;
  };

  // Handler to open the add precaution dialog for a specific hazard
  const handleAddPrecautionToHazard = (hazardId) => {
    const hazard = hazardsData?.find((h) => h.id === hazardId);
    if (hazard) {
      setTargetHazardForNewPrecaution(hazard);
      setPreSelectedCategoryId(null);
      setAddPrecautionDialogOpen(true);
    }
  };

  // Handler to open the add precaution dialog with a pre-selected category
  const handleAddPrecautionWithCategory = (hazardId, categoryId) => {
    const hazard = hazardsData?.find((h) => h.id === hazardId);
    if (hazard) {
      setTargetHazardForNewPrecaution(hazard);
      setPreSelectedCategoryId(categoryId);
      setAddPrecautionDialogOpen(true);
    }
  };

  // Handler to create a new precaution and link it to the hazard
  const handleSaveNewPrecautionToHazard = async (
    precautionName,
    categoryId,
    resetFields
  ) => {
    if (
      !precautionName.trim() ||
      !categoryId ||
      !targetHazardForNewPrecaution
    ) {
      return;
    }

    setIsAddingPrecautionToHazard(true);

    try {
      // Step 1: Create the new precaution
      const language = localStorage.getItem("ln") || "en";
      await axios.post(
        "/api/v1/org-admin/precautions",
        {
          name: precautionName,
          category_id: categoryId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      // Step 2: Refetch precautions to get the newly created one
      await queryClient.invalidateQueries(["precautions"]);
      const updatedPrecautionsResponse = await axios.get(
        "/api/v1/metadata/precautions",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      const updatedPrecautions = updatedPrecautionsResponse.data;

      // Find the newly created precaution by name and category
      const newPrecaution = updatedPrecautions?.find(
        (p) => p.name === precautionName && p.category_id === categoryId
      );

      if (!newPrecaution || !newPrecaution.id) {
        console.error(
          "Could not find newly created precaution in updated list"
        );
        throw new Error("Failed to find new precaution after creation");
      }

      const newPrecautionId = newPrecaution.id;

      // Step 3: Get existing precaution IDs from the hazard
      const existingPrecautionIds =
        targetHazardForNewPrecaution.precaution_categories?.flatMap(
          (category) => category.precautions?.map((p) => p.id) || []
        ) || [];

      // Step 4: Link the new precaution to the hazard (keeping all existing data)
      await axios.put(
        `/api/v1/org-admin/hazards/${targetHazardForNewPrecaution.id}`,
        {
          name: targetHazardForNewPrecaution.name,
          precaution_ids: [...existingPrecautionIds, newPrecautionId],
          likelihood: targetHazardForNewPrecaution.likelihood,
          severity_people: targetHazardForNewPrecaution.severity_people,
          severity_asset: targetHazardForNewPrecaution.severity_asset,
          severity_environment:
            targetHazardForNewPrecaution.severity_environment,
          severity_reputation: targetHazardForNewPrecaution.severity_reputation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      // Success - reset and close
      resetFields();
      setAddPrecautionDialogOpen(false);
      setTargetHazardForNewPrecaution(null);

      setToast({
        open: true,
        message: t("hazards.successAddPrecautionToHazardToast"),
        severity: "success",
      });

      // Refresh data
      queryClient.invalidateQueries(["precautions"]);
      queryClient.invalidateQueries(["hazards"]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsAddingPrecautionToHazard(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Compact Enterprise Header */}
      <OrganizationHeader
        icon={WarningIcon}
        title={t("hazards.headerTitle")}
        subtitle={t("hazards.headerSubtitle")}
        count={hazardsData?.length || 0}
        onHelpClick={() => setHelpModalOpen(true)}
        helpTooltip={t("hazards.helpTooltip")}
        helpAriaLabel={t("hazards.helpButtonAria")}
        actions={[
          {
            label: t("hazards.addButton"),
            icon: <AddIcon />,
            onClick: () => setAddDialogOpen(true),
            ariaLabel: t("hazards.addButtonAria"),
          },
        ]}
      />

      <Box sx={{ px: 4, pb: 4 }}>
        {/* Conditional Layout: Accordion for small screens, Split Panel for large screens */}
        {isSmallScreen ? (
          <MobileHazardsView
            workflowEntries={workflowEntries}
            expandedHazard={expandedHazard}
            onHazardToggle={setExpandedHazard}
            onEditHazard={handleEditDialogOpen}
            onUnlinkHazard={confirmUnlinkHazard}
            onDeleteHazard={confirmDelete}
            onEditWorkflow={handleEditWorkflowDialogOpen}
            getRiskLevelColor={getRiskLevelColor}
            onUnlinkPrecaution={confirmUnlinkPrecaution}
            deletingPrecaution={deletingPrecaution}
            onAddPrecaution={handleAddPrecautionToHazard}
            onAddPrecautionWithCategory={handleAddPrecautionWithCategory}
            isLoading={hazardsLoading || workflowsLoading}
            hasError={hazardsError || workflowsError}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              minHeight: "calc(100vh - 400px)",
            }}
          >
            <WorkflowsList
              workflowEntries={workflowEntries}
              selectedWorkflowId={selectedWorkflowId}
              onWorkflowSelect={setSelectedWorkflowId}
              isLoading={hazardsLoading || workflowsLoading}
              hasError={hazardsError || workflowsError}
            />
            <HazardsPanel
              selectedWorkflowGroup={selectedWorkflowGroup}
              selectedWorkflowId={selectedWorkflowId}
              expandedHazard={expandedHazard}
              onHazardToggle={setExpandedHazard}
              onEditHazard={handleEditDialogOpen}
              onUnlinkHazard={confirmUnlinkHazard}
              onDeleteHazard={confirmDelete}
              onEditWorkflow={handleEditWorkflowDialogOpen}
              getRiskLevelColor={getRiskLevelColor}
              onUnlinkPrecaution={confirmUnlinkPrecaution}
              deletingPrecaution={deletingPrecaution}
              onAddPrecaution={handleAddPrecautionToHazard}
              onAddPrecautionWithCategory={handleAddPrecautionWithCategory}
            />
          </Box>
        )}
      </Box>

      {/* Add Hazard Dialog */}
      <AddHazardDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={addHazard}
        precautionOptions={precautionOptions}
        precautionsLoading={precautionsLoading}
        categoriesLoading={categoriesLoading}
        isLoading={postIsLoading}
        onShowPrecautionsPopup={(precautions, updateCallback) => {
          setSelectedPrecautions(precautions);
          setSelectedPrecautionsUpdateCallback(() => updateCallback);
          setShowPrecautionsPopup(true);
        }}
      />

      {/* Edit Hazard Dialog */}
      <EditHazardDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        onSave={editHazard}
        hazard={editingHazard}
        precautionOptions={precautionOptions}
        precautionsLoading={precautionsLoading}
        categoriesLoading={categoriesLoading}
        isLoading={editIsLoading}
        onShowPrecautionsPopup={(precautions, updateCallback) => {
          setSelectedPrecautions(precautions);
          setSelectedPrecautionsUpdateCallback(() => updateCallback);
          setShowPrecautionsPopup(true);
        }}
      />

      {/* Selected Precautions Popup */}
      <SelectedPrecautionsPopup
        open={showPrecautionsPopup}
        onClose={() => setShowPrecautionsPopup(false)}
        selectedPrecautions={selectedPrecautions}
        onRemovePrecaution={(precautionValue) => {
          const updatedPrecautions = selectedPrecautions.filter(
            (p) => p.value !== precautionValue
          );
          setSelectedPrecautions(updatedPrecautions);
          if (selectedPrecautionsUpdateCallback) {
            selectedPrecautionsUpdateCallback(updatedPrecautions);
          }
        }}
        onClearAll={() => {
          setSelectedPrecautions([]);
          if (selectedPrecautionsUpdateCallback) {
            selectedPrecautionsUpdateCallback([]);
          }
        }}
        precautionOptions={precautionOptions}
      />

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemType={t("hazards.itemType")}
      />

      {/* Unlink Hazard Confirmation Dialog */}
      <UnlinkHazardDialog
        open={unlinkConfirmOpen}
        onClose={() => setUnlinkConfirmOpen(false)}
        onConfirm={handleUnlinkHazard}
        hazard={unlinkingHazard}
      />

      {/* Unlink Precaution Confirmation Dialog */}
      <UnlinkPrecautionDialog
        open={unlinkPrecautionConfirmOpen}
        onClose={() => setUnlinkPrecautionConfirmOpen(false)}
        onConfirm={handleDeletePrecaution}
        precaution={unlinkingPrecaution}
      />

      {/* Edit Workflow Hazards Dialog */}
      <EditWorkflowHazardsDialog
        open={editWorkflowDialogOpen}
        onClose={handleEditWorkflowDialogClose}
        onSave={handleSaveWorkflowHazards}
        workflow={editingWorkflow}
        allHazards={hazardsData}
        allWorkflows={workflowsData}
        selectedHazardIds={selectedWorkflowHazards}
        onToggleHazard={handleWorkflowHazardToggle}
        isLoading={editWorkflowIsLoading}
        hazardsLoading={hazardsLoading}
      />

      {/* Add Precaution to Hazard Dialog */}
      <AddPrecautionDialog
        open={addPrecautionDialogOpen}
        onClose={() => {
          setAddPrecautionDialogOpen(false);
          setTargetHazardForNewPrecaution(null);
          setPreSelectedCategoryId(null);
        }}
        onSave={handleSaveNewPrecautionToHazard}
        categories={precautionCategories}
        categoriesLoading={categoriesLoading}
        isLoading={isAddingPrecautionToHazard}
        preSelectedCategoryId={preSelectedCategoryId}
      />

      {/* Hazards Guide Dialog */}
      <HazardsGuideDialog
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      {/* Toast Notification */}
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
}

export default Hazards;
